import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import HierarchicalOpalGrid, {
  useGridExpandState
} from '../DenaliDataPipeline/report/HierarchicalOpalGrid'
import Select from '../../molecules/Select'
import SydButton from '../../commonDesign/Button'
import FlexColumn from '../../molecules/FlexColumn'
import { localStorageHelper } from '../../../utils/localStorageHelper'
import { useGetBlueprintLatchDates } from '../../../api/denali'
import useBlueprintData from './hooks/useBlueprintData'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import GridColumnConfigModal from './GridColumnConfigModal'
import DynamicFilterModal from './DynamicFilterModal'
import BlueprintExportButton from './BlueprintExportButton'
dayjs.extend(utc)

/**
 * Builds Select options from subordinate predicate definitions.
 */
function buildPredicateOptions (subordinatePredicates, description) {
  const options = subordinatePredicates.map(p => ({
    label: p.description || p.code,
    value: p.code
  }))
  options.splice(0, 0, { label: description || 'All', value: null })
  return options
}

/**
 * Loads saved column config from localStorage. Validates that the saved
 * columns match the blueprint's columns by name. Returns null if invalid.
 */
function loadSavedColumns (storageKey, blueprintColumns) {
  if (!storageKey) return null

  const saved = localStorageHelper.tryLoad(storageKey)
  if (!Array.isArray(saved) || saved.length === 0) return null

  const blueprintNames = new Set(blueprintColumns.map(c => c.name))
  const savedNames = new Set(saved.map(c => c.name))

  if (blueprintNames.size !== savedNames.size) return null
  for (const name of blueprintNames) {
    if (!savedNames.has(name)) return null
  }

  return saved
}

/**
 * Blueprint-driven data grid with server-side pagination and sorting.
 * Includes a title header, optional subordinate predicate selector,
 * and user-configurable column visibility and ordering.
 */
function BlueprintDataGrid ({
  blueprintCode,
  config,
  displayName,
  description,
  subordinatePredicates,
  latch,
  purposeColumns,
  parameters
}) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort,
    localStorageKey
  } = config

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode
  const hasPredicates = subordinatePredicates?.length > 0

  const storageKey = localStorageKey ? `${localStorageKey}.columns` : null

  // User column preferences (order + visibility only), persisted in localStorage
  const [userColumnPrefs, setUserColumnPrefs] = useState(() => {
    return loadSavedColumns(storageKey, config.columns)
  })

  // Merge fresh blueprint metadata onto saved order/visibility preferences.
  // displayName, align, formatString, sortable, filterable, exclude always
  // come from the blueprint; only order and hidden come from localStorage.
  const mergedColumns = useMemo(() => {
    if (!userColumnPrefs) return config.columns

    const blueprintMap = new Map(config.columns.map(c => [c.name, c]))

    return userColumnPrefs.map(saved => {
      const fresh = blueprintMap.get(saved.name)
      if (!fresh) return saved
      return { ...fresh, hidden: saved.hidden }
    })
  }, [config.columns, userColumnPrefs])

  const [configOpen, setConfigOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [dynamicFilters, setDynamicFilters] = useState([])

  // Merge presentation columns with purpose column metadata (dataType, faceted)
  const filterableColumns = useMemo(() => {
    const purposeMap = new Map((purposeColumns || []).map(c => [c.name, c]))
    return config.columns
      .filter(c => c.filterable && !c.exclude)
      .map(c => {
        const purpose = purposeMap.get(c.name)
        return {
          ...c,
          dataType: purpose?.dataType || 'string',
          faceted: purpose?.faceted || false
        }
      })
  }, [config.columns, purposeColumns])

  const handleFiltersApply = useCallback((filters) => {
    setDynamicFilters(filters)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleColumnConfigSave = useCallback((updatedColumns) => {
    setUserColumnPrefs(updatedColumns)
    if (storageKey) localStorageHelper.store(storageKey, updatedColumns)
  }, [storageKey])

  // Server-side pagination state
  const [pageState, setPageState] = useState({ pageIndex: 0, pageSize: 100 })

  // Server-side sort state (tanstack format)
  const [sortState, setSortState] = useState(() => mapDefaultSort(rawDefaultSort))

  // Subordinate predicate selection
  const [selectedPredicate, setSelectedPredicate] = useState(null)

  const predicateOptions = useMemo(() => {
    if (!hasPredicates) return []
    return buildPredicateOptions(subordinatePredicates, description)
  }, [subordinatePredicates, description, hasPredicates])

  const handlePredicateChange = useCallback((value) => {
    setSelectedPredicate(value)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  // Latch date support
  const hasLatch = !!latch
  const latchMode = config.latchMode || 'selectable'
  const { data: latchDatesData } = useGetBlueprintLatchDates(
    blueprintCode,
    purposeCode,
    { enabled: hasLatch, limit: latchMode === 'latest' ? 1 : (latch?.limit ?? 100) }
  )
  const latchDates = useMemo(() => latchDatesData?.dates ?? [], [latchDatesData])
  const [selectedDate, setSelectedDate] = useState(null)

  const effectiveDate = selectedDate ?? latchDates[0] ?? null

  const latchDateOptions = useMemo(() => {
    if (!latchDates.length) return []
    return latchDates.map(d => ({
      label: dayjs.utc(d).format('[As of] MMMM D, YYYY'),
      value: d
    }))
  }, [latchDates])

  const handleDateChange = useCallback((value) => {
    setSelectedDate(value)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  // Merge latch date into parameters
  const effectiveParameters = useMemo(() => {
    if (!hasLatch || !effectiveDate) return parameters
    return { ...parameters, [latch.parameterName]: effectiveDate }
  }, [parameters, hasLatch, latch, effectiveDate])

  const handleSortChange = useCallback((updater) => {
    setSortState(updater)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  const sortBy = sortState?.[0]?.id ?? null
  const sortDirection = sortState?.[0]?.desc ? 'desc' : 'asc'

  const activePredCodes = useMemo(() => {
    return selectedPredicate ? [selectedPredicate] : []
  }, [selectedPredicate])

  const { data: mainData, metadata, isLoading: mainLoading, isFetching: mainFetching } = useBlueprintData(
    blueprintCode,
    purposeCode,
    {
      parameters: effectiveParameters,
      page: pageState.pageIndex,
      pageSize: pageState.pageSize,
      sortBy,
      sortDirection,
      subordinatePredicates: activePredCodes,
      filters: dynamicFilters,
      includeCount: true
    }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000 }
  )

  const { columns, includeFooter } = useBlueprintColumnDefs(
    mergedColumns,
    hasFooterPurpose ? footerData : [],
    footerColumnMapping
  )

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  const { expandState, setExpandState } = useGridExpandState(null)

  const isBusy = mainLoading || mainFetching || (hasFooterPurpose && footerLoading)

  return (
    <FlexColumn className='gap-xs'>
      <div className='flex flex-row justify-between items-end'>
        <div className='flex flex-col gap-xs'>
          {displayName
            ? <div className='opal-heading opal-heading-2 font-size-lg'>{displayName}</div>
            : null}
          <div className='flex flex-row gap-md items-center font-size-sm'>
            {hasPredicates
              ? (
                <Select
                  value={selectedPredicate}
                  onChange={handlePredicateChange}
                  options={predicateOptions}
                  placeholder={description || 'All'}
                />
              )
              : null}
            {hasLatch && latchMode === 'latest' && effectiveDate
              ? (
                <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
                  {dayjs.utc(effectiveDate).format('[As of] MMMM D, YYYY')}
                </span>
              )
              : null}
            {hasLatch && latchMode !== 'latest' && latchDateOptions.length > 0
              ? (
                <Select
                  value={effectiveDate}
                  onChange={handleDateChange}
                  options={latchDateOptions}
                  placeholder='Select date'
                />
              )
              : null}
          </div>
        </div>
        <div className='flex flex-row gap-sm'>
          <BlueprintExportButton
            blueprintCode={blueprintCode}
            config={config}
            purposeCode={purposeCode}
            parameters={effectiveParameters}
            filters={dynamicFilters}
            subordinatePredicates={activePredCodes}
            sortBy={sortBy}
            sortDirection={sortDirection}
          />
          <SydButton
            onClick={() => setFilterOpen(true)}
            variant='outline'
            size='xs'
            icon='filters'
          >
            {dynamicFilters.length > 0
              ? `${dynamicFilters.length} Filter${dynamicFilters.length === 1 ? '' : 's'} applied`
              : 'Filters'}
          </SydButton>
          <SydButton
            onClick={() => setConfigOpen(true)}
            variant='outline'
            size='xs'
            icon='settings'
          >
            Configure
          </SydButton>
        </div>
      </div>
      <HierarchicalOpalGrid
        data={mainData || []}
        columns={visibleColumns}
        isFetching={isBusy}
        pageState={pageState}
        onPageChange={setPageState}
        expandState={expandState}
        onExpandStateChange={setExpandState}
        sortState={sortState}
        onSortChange={handleSortChange}
        manualPagination
        manualSorting
        totalRows={metadata?.totalRowCount}
        pageSizeOptions={[50, 100, 250, 500]}
        includeFooter={includeFooter}
      />
      <GridColumnConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        columns={mergedColumns}
        defaultColumns={config.columns}
        onSave={handleColumnConfigSave}
      />
      <DynamicFilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        columns={filterableColumns}
        blueprintCode={blueprintCode}
        purposeCode={purposeCode}
        activeFilters={dynamicFilters}
        onApply={handleFiltersApply}
      />
    </FlexColumn>
  )
}

BlueprintDataGrid.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    purposeCode: PropTypes.string.isRequired,
    localStorageKey: PropTypes.string,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    variationGroups: PropTypes.arrayOf(PropTypes.string),
    latchMode: PropTypes.oneOf(['selectable', 'latest'])
  }).isRequired,
  displayName: PropTypes.string,
  description: PropTypes.string,
  subordinatePredicates: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    description: PropTypes.string,
    expression: PropTypes.string
  })),
  latch: PropTypes.shape({
    parameterName: PropTypes.string.isRequired,
    dateColumn: PropTypes.string.isRequired,
    dateTable: PropTypes.string.isRequired,
    limit: PropTypes.number
  }),
  purposeColumns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    dataType: PropTypes.string,
    faceted: PropTypes.bool
  })),
  parameters: PropTypes.object
}

BlueprintDataGrid.defaultProps = {
  parameters: {},
  subordinatePredicates: [],
  purposeColumns: []
}

export default BlueprintDataGrid
